import { CODE_MODE_ENTRIES, spacingEntryForArrow, type CodeModeEntry } from "./table";

/** A partially-typed code clears itself so the user is never stranded mid-code. */
const TIMEOUT_MS = 1600;
/** A keystroke that starts no live code flashes briefly, then clears. */
const MISS_FLASH_MS = 550;
/**
 * How long a complete code that is *also* the prefix of a longer one waits
 * before committing. `FT` (Format Tools) is a complete code, but `FTS` (Fit
 * to Slide Stretch) extends it — so `FT` can't fire on its second keystroke
 * without making `FTS` unreachable, and can't simply wait for the abandon
 * timeout either or it would never fire at all. It holds for this long
 * instead: another letter continues to the longer code, silence commits the
 * shorter one. Deliberately well under TIMEOUT_MS so it still feels
 * immediate (BR-4 / AC-5).
 */
const SETTLE_MS = 650;

export interface CodeModeSnapshot {
  readonly armed: boolean;
  readonly buffer: string;
  readonly candidates: readonly CodeModeEntry[];
  readonly miss: boolean;
  /** Set for one tick when a unique code resolves — consumed via
   * `consumeResolved()` by the React hook that dispatches it. */
  readonly resolved: CodeModeEntry | null;
}

const EMPTY: CodeModeSnapshot = { armed: false, buffer: "", candidates: [], miss: false, resolved: null };

/**
 * Plain module-level state, not React state: the leader is handled by
 * codeMode/commands.ts's Office.actions.associate handler, which can fire
 * from the slide before DeckorateModule has ever mounted.
 * DeckorateModule's useCodeMode() hook subscribes to this rather than owning
 * the state itself.
 */
let snapshot: CodeModeSnapshot = EMPTY;
let selectionActive = false;
let timer: number | null = null;
const listeners = new Set<() => void>();

function emit(next: CodeModeSnapshot): void {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

function clearTimer(): void {
  if (timer !== null) {
    window.clearTimeout(timer);
    timer = null;
  }
}

/** Codes gated by `requiresSelection` against the live PowerPoint selection —
 * the two selection-dependent sets the spec calls for, read from one table. */
function liveEntries(): readonly CodeModeEntry[] {
  return CODE_MODE_ENTRIES.filter((entry) => !entry.requiresSelection || selectionActive);
}

/**
 * Code mode on, nothing typed yet — the resting state between codes.
 *
 * Code mode is *sticky*: the leader is a "put me in the pane and turn this
 * on" gesture, not a per-code arming step, so every path that finishes with
 * a code returns here rather than switching off. That way the user presses
 * Alt+Shift+K once and then types code after code. Only Escape, or the pane
 * losing focus, ends it (see `reset`, and DeckorateModule's blur listener).
 */
function armedIdle(): CodeModeSnapshot {
  return { armed: true, buffer: "", candidates: liveEntries(), miss: false, resolved: null };
}

/** Publishes a resolved code while staying armed, so the next code can be
 * typed immediately. `consumeResolved` clears only the `resolved` field. */
function fire(entry: CodeModeEntry): void {
  // TEMPORARY DEBUG (AIVA-7971) — remove before merging.
  console.log("[codemode] CODE RESOLVED ->", entry.code, entry.label);
  clearTimer();
  emit({ ...armedIdle(), resolved: entry });
}

/**
 * Matches `buffer` against the live code set. Five outcomes:
 *
 * - **empty buffer** — mode on, waiting, no timer (sticky; see `armedIdle`)
 * - **unique exact match** — fires at once (`AL` on its second keystroke)
 * - **exact match that other codes extend** — holds for SETTLE_MS then
 *   commits, so `FT` is reachable without shadowing `FTS`
 * - **no match at all** — brief miss flash, then back to waiting
 * - **still ambiguous** — waits, showing the candidates (`C` → CS/CT/CV)
 *
 * Every timeout returns to `armedIdle`, never to `EMPTY`: an abandoned or
 * mistyped code clears the *letters*, not the mode.
 */
function armWith(buffer: string): void {
  clearTimer();
  if (buffer === "") {
    emit(armedIdle());
    return;
  }

  const set = liveEntries();
  const exact = set.find((entry) => entry.code === buffer);
  const prefix = set.filter((entry) => entry.code.startsWith(buffer));

  if (exact) {
    if (prefix.length === 1) {
      fire(exact);
      return;
    }
    // Complete, but a longer code still matches. Show it as pending and let
    // the next keystroke decide: another letter re-enters armWith (clearing
    // this timer) and continues to the longer code, silence commits `exact`.
    emit({ armed: true, buffer, candidates: prefix, miss: false, resolved: null });
    timer = window.setTimeout(() => fire(exact), SETTLE_MS);
    return;
  }
  if (prefix.length === 0) {
    emit({ armed: true, buffer, candidates: [], miss: true, resolved: null });
    timer = window.setTimeout(() => emit(armedIdle()), MISS_FLASH_MS);
    return;
  }
  emit({ armed: true, buffer, candidates: prefix, miss: false, resolved: null });
  timer = window.setTimeout(() => emit(armedIdle()), TIMEOUT_MS);
}

/**
 * The letter for a keydown, read from `event.code` ("KeyB" → "B") rather than
 * `event.key`. On Windows, Ctrl+Alt *is* AltGr, which makes `event.key`
 * layout-dependent: on a UK/international layout, AltGr+<letter> reports the
 * alternate character for that key, so testing `event.key` against [A-Z]
 * silently rejects every valid chord. `event.code` is keyed to the physical
 * key, which is why utils/shortcuts.ts has always matched the legacy nav
 * shortcuts that way.
 */
function letterFromCode(event: KeyboardEvent): string | null {
  const match = /^Key([A-Z])$/.exec(event.code);
  return match ? (match[1] as string) : null;
}

export function getSnapshot(): CodeModeSnapshot {
  return snapshot;
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Called whenever PowerPoint's selection changes (see useCodeMode) — gates
 * which codes are currently live without a second selection-tracking
 * mechanism (TR-7). */
export function setSelectionActive(active: boolean): void {
  selectionActive = active;
}

export function reset(): void {
  // TEMPORARY DEBUG (AIVA-7971) — remove before merging. Prints when code mode
  // switches OFF, including the window-blur path in DeckorateModule.
  console.log("[codemode] reset() — code mode OFF");
  clearTimer();
  emit(EMPTY);
}

/**
 * Drops the letters typed so far while leaving code mode on.
 *
 * The first rung of DeckorateModule's dismiss ladder, and deliberately not
 * `reset`: abandoning a half-typed code should cost the user the letters,
 * not the mode — they retype the code immediately rather than reaching for
 * the leader again. Returns false when there was nothing to clear, which is
 * what tells the caller to fall through to the next rung (go home).
 */
export function clearBuffer(): boolean {
  if (!snapshot.armed || snapshot.buffer === "") return false;
  armWith("");
  return true;
}

/**
 * Removes the last letter of a partial code — Backspace's in-code meaning,
 * so `BC` mistyped as `BX` is one keystroke from recoverable.
 *
 * Returns false when no code is part-typed; DeckorateModule then reads the
 * same keystroke as "go back to the home grid" instead.
 */
export function backspace(): boolean {
  if (!snapshot.armed || snapshot.buffer === "") return false;
  armWith(snapshot.buffer.slice(0, -1));
  return true;
}

/** Consumes a resolved match so it dispatches exactly once. */
export function consumeResolved(): CodeModeEntry | null {
  const resolved = snapshot.resolved;
  if (resolved) emit({ ...snapshot, resolved: null });
  return resolved;
}

/**
 * Turns code mode on with an empty buffer. Two callers, both of them a
 * leader press: codeModeCommands' Office.actions.associate handler (leader
 * pressed while the slide has focus) and TaskPane's document listener
 * (leader pressed with the pane focused, on any tab — FR-3).
 *
 * Pressed once, not per code: the mode then stays on, so the user types
 * `cs`, then `dl`, then `ct` as ordinary bare keystrokes without touching
 * the leader again. `reset()` is the only way out besides losing focus.
 */
export function arm(): void {
  // TEMPORARY DEBUG (AIVA-7971) — remove before merging.
  console.log("[codemode] arm() — code mode ON, buffer cleared");
  armWith("");
}

/**
 * Feeds an in-pane keydown into the accumulator. Returns `true` when the
 * event was consumed — the caller should preventDefault and stop there,
 * rather than falling back to the nav shortcuts (resolveShortcut).
 *
 * Only two things are consumed: Escape, and a bare letter while code mode is
 * on. Every modifier combination is left untouched, so all six Deckorate nav
 * shortcuts keep working exactly as before (AC-10).
 *
 * The leader itself is *not* handled here — TaskPane owns it, because this
 * function is only reachable while DeckorateModule is mounted (the Deckorate
 * tab) and the leader has to work from any tab.
 */
export function handleKeydown(event: KeyboardEvent): boolean {
  // TEMPORARY DEBUG (AIVA-7971) — remove before merging.
  console.log("[codemode] accumulator sees", {
    code: event.code,
    key: event.key,
    armed: snapshot.armed,
    buffer: snapshot.buffer,
    modifiers: { alt: event.altKey, shift: event.shiftKey, ctrl: event.ctrlKey, meta: event.metaKey },
  });
  if (event.key === "Escape") {
    if (!snapshot.armed) return false;
    reset();
    return true;
  }
  if (!snapshot.armed) return false;
  // Codes are bare letters (BR-1) — any modifier held disqualifies the
  // keystroke, leaving it for whatever else wants it.
  if (event.ctrlKey || event.altKey || event.shiftKey || event.metaKey) return false;

  // "S" then an arrow adjusts spacing — checked before the letter path below,
  // which can never see an arrow (letterFromCode only matches KeyA-KeyZ).
  // Only while the buffer is exactly "S", so the other S* codes still resolve
  // normally on their second letter. An unmatched arrow, or one pressed with
  // nothing selected, falls through unconsumed rather than eating the key.
  if (snapshot.buffer === "S") {
    const spacing = spacingEntryForArrow(event.code);
    if (spacing && (!spacing.requiresSelection || selectionActive)) {
      fire(spacing);
      return true;
    }
  }

  const letter = letterFromCode(event);
  if (!letter) return false;

  armWith(snapshot.buffer + letter);
  return true;
}
